import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type HTMLAttributes,
  type KeyboardEvent
} from 'react'
import clsx from 'clsx'
import { twMerge } from 'tailwind-merge'

export type TileVariant = 'surface' | 'interactive' | 'compact'

const tileRoot = (variant: TileVariant, interactive: boolean) =>
  clsx(
    'relative overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-900',
    'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100',
    variant !== 'compact' && 'shadow-sm dark:shadow-black/30',
    variant === 'compact' && 'shadow-sm dark:shadow-black/30',
    variant === 'interactive' &&
      'transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-950',
    interactive && 'cursor-pointer select-none'
  )

export interface TileProps extends HTMLAttributes<HTMLDivElement> {
  variant?: TileVariant
  /** Klickbare Kachel (div mit role="button", kein verschachteltes <button>) */
  onClick?: () => void
}

/**
 * Einheitliche Kachel-Vorlage für Dashboard, Lernbüro, Skillmap, Listen u. a.
 * Unterkomponenten: Header, Content, Footer, Row, AccentStrip, Main, AccentDot.
 */
export const Tile = forwardRef<HTMLDivElement, TileProps>(function Tile(
  { variant = 'surface', className, onClick, children, tabIndex, ...rest },
  ref
) {
  const interactive = Boolean(onClick)

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick?.()
    }
  }

  return (
    <div
      ref={ref}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? (tabIndex ?? 0) : tabIndex}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={twMerge(tileRoot(variant, interactive), className)}
      {...rest}
    >
      {children}
    </div>
  )
})

Tile.displayName = 'Tile'

export function TileHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx('border-b border-gray-100 px-4 py-3 dark:border-gray-800', className)} {...props} />
}

export function TileContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx('px-4 py-3', className)} {...props} />
}

export function TileFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx('border-t border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-950/80', className)}
      {...props}
    />
  )
}

/** Zeilen-Layout: z. B. Klassenarbeit / Liste mit linker Streifen-Spalte */
export function TileRow({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx('flex min-h-0 items-stretch', className)} {...props} />
}

/** Linker Akzent-Streifen (Icon/Grip); Farbe über Tailwind am Aufrufer überschreibbar */
export function TileAccentStrip({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        'flex w-10 shrink-0 items-center justify-center rounded-l-lg bg-gradient-to-b from-slate-500 to-slate-600 text-white',
        className
      )}
      {...props}
    />
  )
}

export function TileMain({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx('min-w-0 flex-1', className)} {...props} />
}

export interface TileAccentDotProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Tailwind-Hintergrund z. B. bg-primary-500 */
  colorClassName?: string
  /** Nur wenn nötig (Lernplan-Farben aus API): ein Punkt mit Hex — Ausnahme wie in ui-rules dokumentiert */
  accentHex?: string
}

export function TileAccentDot({ className, colorClassName, accentHex, ...props }: TileAccentDotProps) {
  return (
    <div
      className={clsx(
        'pointer-events-none absolute left-2 top-2 z-[1] h-3 w-3 rounded-full ring-2 ring-white dark:ring-gray-900',
        !accentHex && (colorClassName ?? 'bg-primary-600'),
        className
      )}
      style={accentHex ? { backgroundColor: accentHex } : undefined}
      aria-hidden
      {...props}
    />
  )
}

/** Quadratische Kachel (z. B. Skillmap-Raster) — Inhalt zentrieren */
export function TileSquareBody({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={clsx(
        'flex aspect-square min-h-[120px] max-h-[200px] w-full flex-col items-center justify-center p-3 text-center',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export interface TileTitleProps extends HTMLAttributes<HTMLParagraphElement> {
  as?: 'h2' | 'h3' | 'p'
}

export function TileTitle({ className, as: Tag = 'h3', ...props }: TileTitleProps) {
  return <Tag className={clsx('text-sm font-semibold text-gray-900 dark:text-gray-100', className)} {...props} />
}

export function TileDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={clsx('text-xs text-gray-500 dark:text-gray-400', className)} {...props} />
}
